
import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import AdminHeader from "../../Header/AdminHeader";
import AdminFooter from "../../Footer/AdminFooter";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import styles from "./UserPlan.module.css";
import { userloginDetails } from "../../Utils/AdminAuth";
import {
  fetchUserAssignedPlans,
  updateMainPlan,
  updateAddOnPlan,
} from "./UserPlanApi";

const MAIN_PLAN_ALLOWED_FIELDS = [
  "amount_eur",
  "amount_inr",
  "amount_usd",
  "authkey_branding",
  "finalAmount",
  "human_agent_seats",
  "included_ai_tokens",
  "platform_fee_waiver_amount",
  "platform_fee_waiver_enabled",
  "plan_description",
  "plan_duration",
  "plan_for",
  "plan_title",
  "plan_type",
  "startDate",
  "endDate",
  "status",
  "whatsapp_chat_widget",
];

const ADD_ON_ALLOWED_FIELDS = [
  "value",
  "plan_duration",
  "start_date",
  "end_date",
  "plan_for",
  "amount_inr",
  "amount_usd",
  "amount_eur",
];

const PLAN_DURATIONS = ["monthly", "quarterly", "yearly"];
const PLAN_FOR_OPTIONS = ["monthly_cost", "widget", "other"];

const prettifyKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const EditUserPlan = () => {
  const { id: retrUserId, userType: selectedUserType } = useParams();

  const lStorage = userloginDetails();
  const user_id = lStorage?.user?.userId;
  const token = lStorage?.token;
  const userType = lStorage?.user?.isAdmin;
  const subAdminPermission = lStorage?.permission?.per_value?.usermanagement?.children;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAddOn, setSavingAddOn] = useState(null); // key of add-on currently saving
  const [message, setMessage] = useState({ type: "", text: "" });

  const [planId, setPlanId] = useState(null);
  const [mainPlan, setMainPlan] = useState({
    plan_title: "",
    plan_for: "",
    plan_duration: "",
    plan_description: "",
    amount_inr: "",
    amount_usd: "",
    amount_eur: "",
    plan_type: "",
    channel: "", // read-only
    platform_fee_waiver_enabled: 0,
    platform_fee_waiver_amount: "",
    human_agent_seats: "",
    included_ai_tokens: "",
    whatsapp_chat_widget: "",
    authkey_branding: "",
    finalAmount: "",
    startDate: "",
    endDate: "",
  });

  // const [showAdvanced, setShowAdvanced] = useState(false);
  // const [advanced, setAdvanced] = useState({
  //   human_agent_seats: "",
  //   included_ai_tokens: "",
  //   whatsapp_chat_widget: "",
  //   authkey_branding: "",
  //   finalAmount: "",
  //   startDate: "",
  //   endDate: "",
  // });

  const [addOns, setAddOns] = useState({}); // { [addOnKey]: { ...fields } }

  const loadPlan = useCallback(() => {
    if (!user_id || !token || !retrUserId) return;

    setLoading(true);
    setMessage({ type: "", text: "" });

    fetchUserAssignedPlans({
      userId: user_id,
      token,
      filterType: "user_wise",
      retrUserId,
    })
      .then((result) => {
        const plan = result?.data?.[0];
        if (!plan) {
          setMessage({
            type: "danger",
            text: "No plan found for this user.",
          });
          setLoading(false);
          return;
        }

        setPlanId(plan.plan_id);
        setMainPlan({
          plan_title: plan.plan_title ?? "",
          plan_for: plan.plan_for ?? "",
          plan_duration: plan.plan_duration ?? "",
          plan_description: plan.plan_description ?? "",
          amount_inr: plan.amount_inr ?? "",
          amount_usd: plan.amount_usd ?? "",
          amount_eur: plan.amount_eur ?? "",
          plan_type: plan.plan_type ?? "",
          channel: plan.channel ?? "",
          status: plan.status === 0 ? "inactive" : "active",
          platform_fee_waiver_enabled: plan.platform_fee_waiver_enabled ?? 0,
          platform_fee_waiver_amount: plan.platform_fee_waiver_amount ?? "",
          authkey_branding: plan.authkey_branding ?? "",
          human_agent_seats: plan.human_agent_seats ?? "",
          included_ai_tokens: plan.included_ai_tokens ?? "",
          whatsapp_chat_widget: plan.whatsapp_chat_widget ?? "",
          finalAmount: plan.finalAmount ?? "",
          startDate: plan.startDate ?? "",
          endDate: plan.endDate ?? "",
        });

        const initialAddOns = {};
        Object.entries(plan.add_on || {}).forEach(([key, addOn]) => {
          initialAddOns[key] = {
            add_on_id: addOn.add_on_id,
            value: addOn.value ?? "",
            amount_inr: addOn.amount ?? addOn.amount_inr ?? "",
            amount_usd: addOn.amount_usd ?? "",
            amount_eur: addOn.amount_eur ?? "",
            plan_for: addOn.plan_for ?? "",
            plan_duration: addOn.plan_duration ?? "",
            start_date: addOn.start_date ?? "",
            end_date: addOn.end_date ?? "",
          };
        });
        setAddOns(initialAddOns);

        setLoading(false);
      })
      .catch((err) => {
        setMessage({
          type: "danger",
          text: err?.message || "Failed to load user plan.",
        });
        setLoading(false);
      });
  }, [user_id, token, retrUserId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const handleMainChange = (field, value) => {
    setMainPlan((prev) => ({ ...prev, [field]: value }));
  };

  // const handleAdvancedChange = (field, value) => {
  //   setAdvanced((prev) => ({ ...prev, [field]: value }));
  // };

  const handleAddOnChange = (key, field, value) => {
    setAddOns((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const saveMainPlan = async () => {
    if (!planId) {
      setMessage({ type: "danger", text: "Missing plan id — cannot save." });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    const fields = {
      plan_title: mainPlan.plan_title,
      plan_for: mainPlan.plan_for,
      plan_duration: mainPlan.plan_duration,
      plan_description: mainPlan.plan_description,
      amount_inr: mainPlan.amount_inr === "" ? undefined : Number(mainPlan.amount_inr),
      amount_usd: mainPlan.amount_usd === "" ? undefined : Number(mainPlan.amount_usd),
      amount_eur: mainPlan.amount_eur === "" ? undefined : Number(mainPlan.amount_eur),
      plan_type: mainPlan.plan_type,
      status: mainPlan.status === "active" ? 1 : 0,
      platform_fee_waiver_enabled: mainPlan.platform_fee_waiver_enabled ? 1 : 0,
      platform_fee_waiver_amount:
        mainPlan.platform_fee_waiver_amount === ""
          ? undefined
          : Number(mainPlan.platform_fee_waiver_amount),
      human_agent_seats: mainPlan.human_agent_seats === "" ? undefined : Number(mainPlan.human_agent_seats),
      included_ai_tokens: mainPlan.included_ai_tokens === "" ? undefined : Number(mainPlan.included_ai_tokens),
      whatsapp_chat_widget: mainPlan.whatsapp_chat_widget === "" ? undefined : Number(mainPlan.whatsapp_chat_widget),
      authkey_branding: mainPlan.authkey_branding === "" ? undefined : Number(mainPlan.authkey_branding),
      finalAmount: mainPlan.finalAmount === "" ? undefined : Number(mainPlan.finalAmount),
      startDate: mainPlan.startDate || undefined,
      endDate: mainPlan.endDate || undefined,
    };

    // if (showAdvanced) {
    //   Object.entries(advanced).forEach(([key, value]) => {
    //     if (value !== "") fields[key] = value;
    //   });
    // }

    // Drop undefined values and anything not in the allowed list, as a safety net.
    const cleanedFields = Object.fromEntries(
      Object.entries(fields).filter(
        ([key, value]) => value !== undefined && MAIN_PLAN_ALLOWED_FIELDS.includes(key)
      )
    );

    try {
      const result = await updateMainPlan({
        userId: user_id,
        token,
        retrUserId,
        planId,
        fields: cleanedFields,
      });
      setMessage({
        type: "success",
        text: result?.message || "Main plan updated successfully.",
      });
    } catch (err) {
      setMessage({ type: "danger", text: err?.message || "Failed to update main plan." });
    } finally {
      setSaving(false);
    }
  };

  const saveAddOn = async (addOnKey) => {
    if (!planId) {
      setMessage({ type: "danger", text: "Missing plan id — cannot save." });
      return;
    }

    const addOn = addOns[addOnKey];
    setSavingAddOn(addOnKey);
    setMessage({ type: "", text: "" });

    const fields = {
      value: addOn.value === "" ? undefined : addOn.value,
      plan_duration: addOn.plan_duration || undefined,
      start_date: addOn.start_date || undefined,
      end_date: addOn.end_date || undefined,
      plan_for: addOn.plan_for || undefined,
      amount_inr: addOn.amount_inr === "" ? undefined : Number(addOn.amount_inr),
      amount_usd: addOn.amount_usd === "" ? undefined : Number(addOn.amount_usd),
      amount_eur: addOn.amount_eur === "" ? undefined : Number(addOn.amount_eur),
    };

    const cleanedFields = Object.fromEntries(
      Object.entries(fields).filter(
        ([key, value]) => value !== undefined && ADD_ON_ALLOWED_FIELDS.includes(key)
      )
    );

    try {
      const result = await updateAddOnPlan({
        userId: user_id,
        token,
        retrUserId,
        planId,
        addOnKey,
        fields: cleanedFields,
      });
      setMessage({
        type: "success",
        text: result?.message || `${prettifyKey(addOnKey)} add-on updated successfully.`,
      });
    } catch (err) {
      setMessage({
        type: "danger",
        text: err?.message || `Failed to update ${prettifyKey(addOnKey)} add-on.`,
      });
    } finally {
      setSavingAddOn(null);
    }
  };

  return (
    <div className="wrapper">
      <AdminLeftMenu />
      <div className="main">
        <AdminHeader />
        <main className="content">
          <div className="container-fluid p-0">
            <div className="row mb-2 mb-xl-3">
              <div className="col-auto d-none d-sm-block">
                <h3>
                  Edit User Plan{" "}
                  {mainPlan.plan_title && (
                    <strong className="text-info">{mainPlan.plan_title}</strong>
                  )}
                </h3>
              </div>
            </div>

            {message.text && (
              <div
                className={`alert alert-${message.type} alert-dismissible`}
                role="alert"
              >
                <button
                  type="button"
                  className="close"
                  data-dismiss="alert"
                  aria-label="Close"
                  onClick={() => setMessage({ type: "", text: "" })}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                {message.text}
              </div>
            )}

            <div className="row">
              <div className="card w-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <nav className="text-center" aria-label="breadcrumb">
                      <ol className="breadcrumb bg-transparent p-0 mt-1 mb-0">
                        <Link to={`/admin/user-details/${retrUserId}/${selectedUserType}`}>
                          <li className="breadcrumb-item">User Details</li>
                        </Link>

                        {(userType === "admin" || userType === "reseller" || userType === "emp" || (userType === "subadmin" && subAdminPermission?.transactiondetails)) && (
                          <Link to={`/admin/user-transaction-details/${retrUserId}/${selectedUserType}`}>
                            <li className="breadcrumb-item">Transaction Details</li>
                          </Link>
                        )}

                        {(userType === "admin" || userType === "reseller" || userType === "emp" || (userType === "subadmin" && subAdminPermission?.addeditprice)) && (
                          <Link to={`/admin/user-price-details/${retrUserId}/${selectedUserType}?channel=whatsapp`}>
                            <li className="breadcrumb-item">Add Price</li>
                          </Link>
                        )}

                        {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.awssettings)) && (
                          <Link to={`/admin/aws-settings/${retrUserId}/${selectedUserType}`}>
                            <li className="breadcrumb-item">AWS Settings</li>
                          </Link>
                        )}

                        {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.billingmode)) && (
                          <Link to={`/admin/user-billing-mode/${retrUserId}/${selectedUserType}`}>
                            <li className="breadcrumb-item">Billing Mode</li>
                          </Link>
                        )}

                        {(userType === "admin" || userType === "reseller" || userType === "emp" || (userType === "subadmin" && subAdminPermission?.addeditcredit)) && (
                          <Link to={`/admin/user-add-price/${retrUserId}/${selectedUserType}`}>
                            <li className="breadcrumb-item">Add Credit</li>
                          </Link>
                        )}

                        {userType === "reseller" ? null : (
                          <>
                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.entitybypass)) && (
                              <Link to={`/admin/user-entity-pass/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">Entity By Pass</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.changecurrency)) && (
                              <Link to={`/admin/change-currency/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">Change Currency</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.bulksmsroutesetting)) && (
                              <Link to={`/admin/bulk-sms-route/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">Bulk Sms Route Setting</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.autoshorturl)) && (
                              <Link to={`/admin/api-auto-short-url/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">Auto Short URL</li>
                              </Link>
                            )}

                            {(userType === "admin" || userType === "emp" || (userType === "subadmin" && subAdminPermission?.extrasettings)) && (
                              <Link to={`/admin/extra-settings/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">Extra Settings</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.extrasettings)) && (
                              <Link to={`/admin/mmlite/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">MMLite</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.ai_bot)) && (
                              <Link to={`/admin/ai-bot/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item">AI Bot</li>
                              </Link>
                            )}

                            {(userType === "admin" || (userType === "subadmin" && subAdminPermission?.edituserplan)) && (
                              <Link to={`/admin/edit-user-plan/${retrUserId}/${selectedUserType}`}>
                                <li className="breadcrumb-item active">Edit User Plan</li>
                              </Link>
                            )}
                          </>
                        )}
                      </ol>
                    </nav>
                  </h5>
                </div>

                <div className="card-body">
                  {loading ? (
                    <p>Loading plan details…</p>
                  ) : !planId ? (
                    <p>No plan is currently assigned to this user.</p>
                  ) : (
                    <>
                      {/* ---------- Main plan ---------- */}
                      <h5 className="mb-3">Main Plan</h5>
                      <div className="row">
                        <div className="mb-3 col-md-4">
                          <label className="form-label">Plan Title</label>
                          <input
                            type="text"
                            className="form-control"
                            value={mainPlan.plan_title}
                            onChange={(e) => handleMainChange("plan_title", e.target.value)}
                          />
                        </div>

                        <div className="mb-3 col-md-4">
                          <label className="form-label">Channel</label>
                          <input
                            type="text"
                            className="form-control"
                            value={mainPlan.channel}
                            readOnly
                            title="Channel isn't editable."
                          />
                        </div>

                        <div className="mb-3 col-md-4">
                          <label className="form-label">Status</label>
                          <select
                            className="form-control"
                            value={mainPlan.status}
                            onChange={(e) => handleMainChange("status", e.target.value)}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>

                      <div className="row">
                        <div className="mb-3 col-md-4">
                          <label className="form-label">Plan For</label>
                          <select
                            className="form-control"
                            value={mainPlan.plan_for}
                            onChange={(e) => handleMainChange("plan_for", e.target.value)}
                          >
                            <option value="">Select</option>
                            {PLAN_FOR_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {prettifyKey(opt)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3 col-md-4">
                          <label className="form-label">Plan Duration</label>
                          <select
                            className="form-control"
                            value={mainPlan.plan_duration}
                            onChange={(e) => handleMainChange("plan_duration", e.target.value)}
                          >
                            <option value="">Select</option>
                            {PLAN_DURATIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {prettifyKey(opt)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3 col-md-4">
                          <label className="form-label">Plan Type</label>
                          <input
                            type="text"
                            className="form-control"
                            value={mainPlan.plan_type}
                            onChange={(e) => handleMainChange("plan_type", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Plan Description</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={mainPlan.plan_description}
                          onChange={(e) => handleMainChange("plan_description", e.target.value)}
                        />
                      </div>

                      <div className="row">
                        <div className="mb-3 col-md-4">
                          <label className="form-label">Amount (INR)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={mainPlan.amount_inr}
                            onChange={(e) => handleMainChange("amount_inr", e.target.value)}
                          />
                        </div>
                        <div className="mb-3 col-md-4">
                          <label className="form-label">Amount (USD)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={mainPlan.amount_usd}
                            onChange={(e) => handleMainChange("amount_usd", e.target.value)}
                          />
                        </div>
                        <div className="mb-3 col-md-4">
                          <label className="form-label">Amount (EUR)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={mainPlan.amount_eur}
                            onChange={(e) => handleMainChange("amount_eur", e.target.value)}
                          />
                        </div>
                        <div className={`row mt-2 ${styles?.advancedBox || ""}`}>
                          {["human_agent_seats", "included_ai_tokens", "whatsapp_chat_widget", "authkey_branding", "finalAmount", "startDate", "endDate"].map((field) => (
                            <div className="mb-3 col-md-4" key={field}>
                              <label className="form-label">{prettifyKey(field)}</label>
                              <input
                                type="text"
                                className="form-control"
                                value={mainPlan[field] ?? ""}
                                onChange={(e) => handleMainChange(field, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>


                      <div className="row">
                        <div className="col-md-4 mt-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="platformFeeWaiverEnabled"
                              checked={!!mainPlan.platform_fee_waiver_enabled}
                              onChange={(e) =>
                                handleMainChange(
                                  "platform_fee_waiver_enabled",
                                  e.target.checked ? 1 : 0
                                )
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="platformFeeWaiverEnabled"
                            >
                              Platform Fee Waiver Enabled
                            </label>
                          </div>
                        </div>
                        {!!mainPlan.platform_fee_waiver_enabled && (
                          <div className="mb-3 col-md-4">
                            <label className="form-label">Platform Fee Waiver Amount</label>
                            <input
                              type="number"
                              className="form-control"
                              value={mainPlan.platform_fee_waiver_amount}
                              onChange={(e) =>
                                handleMainChange("platform_fee_waiver_amount", e.target.value)
                              }
                            />
                          </div>
                        )}
                      </div>

                      {/* <div className="mb-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => setShowAdvanced((prev) => !prev)}
                        >
                          {showAdvanced ? "Hide" : "Show"} advanced fields
                        </button>
                        {showAdvanced && (
                          <div className={`row mt-2 ${styles?.advancedBox || ""}`}>
                            <small className="text-muted col-12 mb-2">
                              These are accepted by updateMainPlan but weren't present in the
                              sample response — verify meaning before relying on them.
                            </small>
                            {Object.keys(advanced).map((field) => (
                              <div className="mb-3 col-md-4" key={field}>
                                <label className="form-label">{prettifyKey(field)}</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={advanced[field]}
                                  onChange={(e) => handleAdvancedChange(field, e.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div> */}

                      <div className="mb-4">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={saveMainPlan}
                        >
                          {saving ? "Saving…" : "Save Main Plan"}
                        </button>
                      </div>

                      <hr />

                      {/* ---------- Add-ons ---------- */}
                      <h5 className="mb-3">Add-ons</h5>
                      {Object.keys(addOns).length === 0 ? (
                        <p className="text-muted">No add-ons on this plan.</p>
                      ) : (
                        Object.entries(addOns).map(([key, addOn]) => (
                          <div className="card mb-3" key={key}>
                            <div className="card-body">
                              <h6 className="mb-3">{prettifyKey(key)}</h6>
                              <div className="row">
                                {Object.prototype.hasOwnProperty.call(addOn, "value") &&
                                  addOn.value !== "" && (
                                    <div className="mb-3 col-md-3">
                                      <label className="form-label">Value</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={addOn.value}
                                        onChange={(e) =>
                                          handleAddOnChange(key, "value", e.target.value)
                                        }
                                      />
                                    </div>
                                  )}
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Amount (INR)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={addOn.amount_inr}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "amount_inr", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Amount (USD)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={addOn.amount_usd}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "amount_usd", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Amount (EUR)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={addOn.amount_eur}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "amount_eur", e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              <div className="row">
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Plan For</label>
                                  <select
                                    className="form-control"
                                    value={addOn.plan_for}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "plan_for", e.target.value)
                                    }
                                  >
                                    <option value="">Select</option>
                                    {PLAN_FOR_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {prettifyKey(opt)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Plan Duration</label>
                                  <select
                                    className="form-control"
                                    value={addOn.plan_duration}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "plan_duration", e.target.value)
                                    }
                                  >
                                    <option value="">Select</option>
                                    {PLAN_DURATIONS.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {prettifyKey(opt)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">Start Date</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="YYYY-MM-DD HH:mm:ss"
                                    value={addOn.start_date}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "start_date", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="mb-3 col-md-3">
                                  <label className="form-label">End Date</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="YYYY-MM-DD HH:mm:ss"
                                    value={addOn.end_date}
                                    onChange={(e) =>
                                      handleAddOnChange(key, "end_date", e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={savingAddOn === key}
                                onClick={() => saveAddOn(key)}
                              >
                                {savingAddOn === key ? "Saving…" : `Save ${prettifyKey(key)}`}
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        <AdminFooter />
      </div>
    </div>
  );
};

export default EditUserPlan;